import * as React from 'react';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type { PipelineRunsResponse } from '../types/source-system';

export interface UsePipelineRunsResult {
  data: PipelineRunsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetches recent pipeline runs for a specific project_integration_resource.
 *
 * Re-fetches whenever `resourceUuid` or `refreshKey` changes.
 * `refreshKey` can be bumped by the parent to trigger a reload (e.g. after sync).
 */
export function usePipelineRuns(
  projectUuid: string,
  resourceUuid: string | null,
  limit = 20,
  refreshKey?: string | number | null
): UsePipelineRunsResult {
  const [data, setData] = React.useState<PipelineRunsResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [tick, setTick] = React.useState(0);

  const refresh = React.useCallback(() => setTick((t) => t + 1), []);

  React.useEffect(() => {
    if (!resourceUuid) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void sourceSystemApi
      .getPipelineRuns(projectUuid, resourceUuid, limit)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError((err as Error).message ?? 'Failed to load pipeline runs');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // refreshKey and tick are intentional deps to support manual refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid, resourceUuid, limit, refreshKey, tick]);

  return { data, loading, error, refresh };
}
